import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { map, startWith, takeUntil, tap } from 'rxjs/operators';

import { OrbiAgentInputMethod } from '@patient-ui/patient-web/store';

import { OrbiAnnouncerService } from '../../services/orbi-announcer.service';

/**
 * Minimal shape of the browser SpeechRecognition result we read. Declared
 * locally because the DOM lib's typings for the Web Speech API are not
 * available under this tsconfig.
 */
interface OrbiSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  /** Cancels recognition WITHOUT emitting a final `onresult` (unlike `stop()`). */
  abort(): void;
  onresult:
    | ((event: {
        results: ArrayLike<ArrayLike<{ transcript: string }>>;
      }) => void)
    | null;
  /**
   * Fires when the service stops detecting SPEECH — the gap that ends the
   * listening state — ahead of `onend`, which waits on the final transcript.
   */
  onspeechend: (() => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

/**
 * Orbi message input (PCSA-13160).
 *
 * Presentational text input + send action + voice (mic) input. Emits
 * `send(text)` when the user submits a non-empty/whitespace message via the
 * send button or Enter (Shift+Enter inserts a newline — parity with mobile's
 * `TextInputAction.send`). No max length (parity). On submit the field clears.
 *
 * The field is multi-line and sizes itself to the prompt (PCSA-13548): it starts
 * one line tall, grows a line at a time as the text wraps, stops at the
 * stylesheet's cap and scrolls beyond it, and returns to one line when it clears.
 * `autoGrow` is where that happens and why it needs script at all. The entry-bar
 * variant (`singleLine`) is the exception — one line, truncated.
 *
 * The field + mic are NOT disabled while the agent is thinking (PCSA-13312, 08/14
 * Vibe: "the mic is disabled while the agent is thinking; it should remain
 * accessible during loading", and the user must be able to send a second message
 * mid-response). `disabled` now means the chat is LOCKED, nothing else — the host
 * binds it to the guardrail lock alone.
 *
 * Voice: uses the browser Web Speech API (`SpeechRecognition` /
 * `webkitSpeechRecognition`). Tapping the mic starts listening (placeholder ->
 * "Listening…"); the recognized transcript POPULATES the field as editable text
 * and is NOT auto-sent (parity with mobile). Browsers without the API hide the
 * mic affordance entirely (graceful degradation, e.g. Firefox).
 */
@Component({
  selector: 'patient-ui-orbi-message-input',
  templateUrl: './orbi-message-input.component.html',
  styleUrls: ['./orbi-message-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiMessageInputComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Inerts the field + actions (send, mic). Every consumer now binds this to the
   * guardrail lock ONLY — deliberately NOT to "a response is loading"
   * (PCSA-13312). It stays a separate input from `locked` because `locked` also
   * carries the lock's PRESENTATION (placeholder, struck mic, tooltip), and this
   * is the plain "no interaction" state on its own.
   */
  @Input() disabled = false;

  /**
   * Session-locked state (PCSA-13170). Distinct from `disabled`: when a locking
   * guardrail (EMERGENCY / HARM) holds the session, the field shows the "Feature
   * Unavailable" placeholder, the mic is struck through, and a trailing info
   * affordance reveals a tooltip explaining the lock with a "sign out" link
   * (emits `signOut`). Locking also implies `disabled` (the host sets both), so
   * send/voice stay inert.
   */
  @Input() locked = false;

  /**
   * Optional leading icon image shown at the start of the field in its resting
   * state — the Orbi AI mark (`assets/images/orbi-ai-icon.svg`). Used by the
   * on-page entry point (PCSA-13157) to signal an AI experience per the "Ask a
   * question" design; unset (default) for the in-panel input, which shows no
   * leading icon.
   */
  @Input() leadingIconSrc?: string;

  /**
   * Accessible label / placeholder for the field. Defaults to the design copy
   * ("Ask a question"); overridable so the same component can serve both the
   * entry point and the in-panel input without divergence.
   */
  @Input() placeholder = 'Ask a Question';

  /**
   * When true the field stays on a single line and truncates instead of
   * wrapping/growing — the compact entry-point bar (PCSA-13157). Default false
   * keeps the in-panel input's multi-line behavior (Shift+Enter newline,
   * PCSA-13160 parity).
   */
  @Input() singleLine = false;

  /**
   * When true, the resting field shows a trailing "history" affordance after
   * the mic — a divider then a clock icon (PCSA-13312, Figma "Agent fields").
   * Used by the entry point so a user can jump straight into their past
   * conversations; the in-panel input leaves it off (default). Like the mic, it
   * only shows in the resting state (hidden once there is text to send).
   */
  @Input() showHistory = false;

  /**
   * When true, the resting field shows a "prompt" affordance — the Figma
   * suggestion bulb — between the mic and the trailing history icon
   * (static-prompts MVP, Figma "Make Sense of Your Results"). It re-opens the
   * static-prompt popover after the patient dismisses it.
   *
   * Gated OFF by default so this shared input is unaffected in the in-panel
   * context: ONLY the entry bar (`patient-ui-orbi-entry-button`) sets
   * `[showPromptToggle]="true"`, so the bulb never renders in the chat panel's
   * own input. Like the mic and history, it only shows in the resting state
   * (hidden once there is text to send or while listening).
   */
  @Input() showPromptToggle = false;

  /**
   * When true, the suggestion bulb uses the filled-disc Figma asset (popover
   * open). When false, it uses the plain navy bulb (popover dismissed).
   */
  @Input() promptToggleActive = false;

  /** Emits the trimmed message text on a valid submit. */
  @Output() send = new EventEmitter<string>();

  /** Emits when the user taps the trailing history affordance (PCSA-13312). */
  @Output() history = new EventEmitter<void>();

  /**
   * Emits when the user taps the bulb (prompt-toggle) affordance (static-prompts
   * MVP). Only meaningful when `showPromptToggle` is set — the entry bar handles
   * it to re-open the static-prompt popover. The in-panel input never renders
   * the bulb, so it never emits this there.
   */
  @Output() promptToggle = new EventEmitter<void>();

  /**
   * Emits when the user SELECTS the field — focuses it, or taps the leading Orbi
   * mark, which focuses it (PCSA-13441). Distinct from `send`: nothing has been
   * submitted, the user has only entered the input. The entry point uses it to
   * open an already-active conversation instead of starting a new question; the
   * in-panel input leaves it unbound, so focusing there behaves as before.
   *
   * Deliberately NOT emitted by the mic, history or send buttons: clicking a
   * button does not focus the textarea, so voice keeps working from the entry
   * bar even once a conversation is active (PCSA-13441 AC).
   */
  @Output() activate = new EventEmitter<void>();

  /**
   * Emits when the user activates the "sign out" link in the locked-session
   * tooltip (PCSA-13170). The host dispatches the app logout so the user can
   * sign back in to an unlocked session (the tooltip's literal instruction).
   */
  @Output() signOut = new EventEmitter<void>();

  /**
   * Emits whether the field is currently empty (whitespace-only counts as
   * empty), so the host can show/hide the suggested prompts (PCSA-13159): shown
   * when empty, hidden once the user types.
   */
  @Output() emptyChange = new EventEmitter<boolean>();

  @ViewChild('field') private field?: ElementRef<HTMLTextAreaElement>;

  @ViewChild('lockedInfoTrigger')
  private lockedInfoTrigger?: ElementRef<HTMLButtonElement>;

  @ViewChild('lockedDisclosure')
  private lockedDisclosure?: ElementRef<HTMLElement>;

  readonly control = new FormControl<string>('', { nonNullable: true });

  /** True while voice capture is active (drives the "Listening…" state). */
  listening = false;

  private textSource: OrbiAgentInputMethod = 'typed';

  /** True while the field has focus (drives the gradient "active" border). */
  focused = false;

  lockedInfoOpen = false;

  /** Whether this browser supports the Web Speech API (mic is hidden if not). */
  readonly voiceSupported: boolean = !!this.getSpeechRecognitionCtor();

  private recognition?: OrbiSpeechRecognition;

  /** Teardown signal for the value-changes subscription. */
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly announcer: OrbiAnnouncerService
  ) {}

  ngOnInit(): void {
    // Emit the current empty-state so the host can toggle the suggested prompts
    // (PCSA-13159). `startWith` fires the initial (empty) state on subscribe,
    // covering programmatic `setText`/voice population as well as typing.
    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        // Every route into the field lands here — typing, paste, a voice
        // transcript, a suggested prompt, and the clear on submit — so it is the
        // one place the height has to be re-derived (PCSA-13548). `setValue`
        // writes the view BEFORE it emits, so the textarea already holds the text
        // being measured.
        tap(() => this.autoGrow()),
        map((value) => (value ?? '').trim().length === 0),
        takeUntil(this.destroy$)
      )
      .subscribe((empty) => this.emptyChange.emit(empty));
  }

  ngOnChanges(): void {
    if (!this.locked) {
      this.lockedInfoOpen = false;
    }
  }

  /**
   * Re-fit the field when the viewport changes (PCSA-13548 AC 8).
   *
   * Height is a function of how the text WRAPS, and wrapping is a function of the
   * field's width — which the panel changes at every breakpoint (100% on a tablet,
   * 45vw from lg, 35vw from xl) and a phone changes by being rotated. A prompt that
   * needed three lines in portrait needs two in landscape, and without this the box
   * would keep the taller height it was last measured at, leaving an empty line
   * under the text.
   */
  @HostListener('window:resize')
  onResize(): void {
    this.autoGrow();
  }

  /**
   * Programmatically populate the field (PCSA-13159 — a suggested prompt
   * pre-fills the input WITHOUT auto-sending) and move focus there so the user
   * can edit or press send. `valueChanges` above then reports the non-empty
   * state, hiding the prompts.
   */
  setText(text: string): void {
    this.textSource = 'suggested_prompt';
    this.control.setValue(text);
    this.focus();
    this.cdr.markForCheck();
  }

  get inputMethod(): OrbiAgentInputMethod {
    return this.textSource;
  }

  onUserInput(): void {
    this.textSource = 'typed';
  }

  /** True when the field has non-whitespace text (enables the send action). */
  get canSend(): boolean {
    return !this.disabled && this.control.value.trim().length > 0;
  }

  /**
   * Whether the trailing SEND arrow is rendered (QA 08/23).
   *
   * There has to be something to send — and the mic must not be listening. The
   * send action used to appear on `canSend` alone, and voice populates the field
   * from the FIRST interim result, so the mic vanished behind a send arrow the
   * instant the user started talking while recognition ran on: "speech continues
   * to be transcribed into the input field even when the microphone appears to be
   * off, not in a listening state". Design confirmed the app's behavior is the
   * spec — "the mic is green and in the listening state while the user is talking,
   * when it detects a gap in speech the mic turns into the send arrow button" —
   * and that "having the Send action display during listening state is not
   * expected".
   *
   * The swap is therefore driven by the END of speech, not by the field filling
   * up: `recognition.continuous = false` plus the `onspeechend` handler in
   * `startVoice` drop `listening` on the first gap, and this getter turns into the
   * send arrow at that moment with the transcript already in the field.
   */
  get showSend(): boolean {
    return this.canSend && !this.listening && !this.locked;
  }

  /**
   * Whether the mic is rendered. It holds its place for the whole of the listening
   * state — including while the transcript is filling the field — because it IS
   * the listening indicator (green, `--active`). Outside that it is the resting
   * affordance, so the send arrow takes over as soon as there is text to send.
   * The locked session draws its own struck mic (PCSA-13170), hence `!locked`.
   */
  get showMic(): boolean {
    return this.voiceSupported && !this.locked && !this.showSend;
  }

  /**
   * Show the leading Orbi mark only in the resting state: when a src is set and
   * the field is neither focused (active) nor listening. On focus the mark is
   * hidden and the text caret takes its place (per design).
   */
  get showLeadingIcon(): boolean {
    return !!this.leadingIconSrc && !this.focused && !this.listening;
  }

  /**
   * Placeholder actually rendered on the field. Design review: "when the cursor
   * is active, the text 'Ask a question' should disappear" — so the placeholder
   * is dropped while the field has focus, leaving just the caret. `listening`
   * still wins (it swaps in its own prompt), and the `aria-label` keeps the
   * real placeholder text so the field stays labelled for assistive tech.
   */
  get displayPlaceholder(): string {
    if (this.listening) {
      return 'Listening…';
    }
    return this.focused ? '' : this.placeholder;
  }

  onFocus(): void {
    this.focused = true;
    // "Selecting the field" (PCSA-13441). One emission point for both routes in
    // the AC — the field itself and the Orbi mark, which delegates to `focus()`.
    this.activate.emit();
  }

  onBlur(): void {
    this.focused = false;
  }

  /** Moves focus into the field (PCSA-13169: on resuming a conversation). */
  focus(): void {
    this.field?.nativeElement.focus();
  }

  /** Enter submits; Shift+Enter inserts a newline (handled by the textarea). */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  /**
   * Paste (QA 08/21: "pasting into the Ask AI input leaves the field looking
   * blank; after Send the text appears correctly in the chat — and pasting again
   * shows it twice").
   *
   * Copy a question out of a rendered page, a table cell or a spreadsheet and a
   * TRAILING NEWLINE comes along with it. Both fields showed one line at a time —
   * the entry bar is `--single-line` (24px max-height, overflow hidden) and the
   * in-panel field was a `rows="1"` textarea in a fixed-height bar — so the
   * browser scrolls to keep the caret, now sitting on the empty second line, in
   * view, and the line that was actually pasted scrolls out of sight. Nothing was
   * ever lost: the send button appears (there IS a value) and `submit()` trims,
   * which is why the message arrived intact — and why pasting a second time into
   * the apparently-empty field sent the text twice, on two lines, the message
   * bubble being `white-space: pre-wrap`.
   *
   * So the newlines come out of the pasted text before it lands: collapsed to a
   * space in the single-line bar, trimmed off the ENDS in the multi-line panel
   * field, whose interior newlines are a real feature (Shift+Enter, PCSA-13160).
   * The panel field now grows to fit what it holds (PCSA-13548), which changes the
   * symptom there without removing the cause: a trailing newline no longer hides
   * the paste, it opens an empty line under it and takes the bar a line taller.
   * Trimming the ends is still what stops that.
   * A paste that carries no newline is left entirely to the browser — there is
   * nothing to fix, and the native paste keeps its place in the undo stack.
   */
  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text/plain');
    if (!pasted) {
      return;
    }
    const cleaned = this.withoutStrayNewlines(pasted);
    if (cleaned === pasted) {
      return;
    }
    event.preventDefault();
    this.insertAtCaret(cleaned);
  }

  submit(): void {
    const text = this.control.value.trim();
    if (this.disabled || text.length === 0) {
      return;
    }
    // Stop voice FIRST: a pending interim/final `onresult` firing after we clear
    // the field would repopulate it with the just-sent text. `stopVoice` detaches
    // the handler + aborts, so no late result can arrive.
    this.stopVoice();
    this.send.emit(text);
    this.textSource = 'typed';
    this.control.setValue('');
    // Parity with mobile: drop focus on submit so the user's attention moves to
    // the response rather than staying in the input (mobile `_focusNode.unfocus`).
    this.blur();
  }

  /** Removes focus from the field (parity: called on submit + on response). */
  blur(): void {
    this.field?.nativeElement.blur();
  }

  /** Tapping the trailing history affordance (PCSA-13312). */
  onHistory(): void {
    if (this.disabled) {
      return;
    }
    this.history.emit();
  }

  /**
   * Tapping the bulb (prompt-toggle) affordance (static-prompts MVP). Guarded on
   * `showPromptToggle` as well as `disabled` so a consumer that never opts in
   * can never emit it even if the glyph were somehow reached.
   */
  onPromptToggle(): void {
    if (this.disabled || !this.showPromptToggle) {
      return;
    }
    this.promptToggle.emit();
  }

  /**
   * The "sign out" link inside the locked-session tooltip (PCSA-13170). Emits
   * `signOut` so the host can end the session; only meaningful while locked.
   */
  onSignOut(): void {
    if (!this.locked) {
      return;
    }
    this.signOut.emit();
  }

  toggleLockedInfo(): void {
    this.lockedInfoOpen = !this.lockedInfoOpen;
  }

  onLockedInfoEscape(event: Event): void {
    if (!this.lockedInfoOpen) {
      return;
    }
    event.stopPropagation();
    this.lockedInfoOpen = false;
    this.lockedInfoTrigger?.nativeElement.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.lockedInfoOpen) {
      return;
    }
    const target = event.target as Node | null;
    if (target && this.lockedDisclosure?.nativeElement.contains(target)) {
      return;
    }
    this.lockedInfoOpen = false;
    this.cdr.markForCheck();
  }

  /**
   * Mic tap. Keyed on whether a recognition SESSION is open rather than on
   * `listening`, because the two now come apart: `onspeechend` drops the listening
   * state on the first gap in speech while the session stays open a moment longer
   * to deliver its final transcript. Starting a second session in that window
   * would leave the first one's late `onresult` to overwrite the new one's field.
   * While the user is actually talking the two are the same thing, so the resting
   * / listening behavior is unchanged.
   */
  toggleVoice(): void {
    if (this.disabled) {
      return;
    }
    if (this.recognition) {
      this.stopVoice();
    } else {
      this.startVoice();
    }
  }

  ngOnDestroy(): void {
    this.listening = false;
    this.stopVoice();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startVoice(): void {
    const Ctor = this.getSpeechRecognitionCtor();
    if (!Ctor) {
      return;
    }
    const recognition = new Ctor() as OrbiSpeechRecognition;
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      // Concatenate the recognized chunks into the field as EDITABLE text;
      // do NOT auto-send (the user keeps final control — parity with mobile).
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      this.textSource = 'voice';
      this.control.setValue(transcript);
      this.cdr.markForCheck();
    };
    // A GAP IN SPEECH ends the listening state (QA 08/23): the mic stops being
    // green and the send arrow takes its place, with the transcript sitting in the
    // field ready to send — the app's behavior, which design confirmed as the
    // spec. `continuous = false` above already makes the service end the session on
    // that gap, but its `onend` trails the gap by however long the final result
    // takes to come back, and the mic must not stay green through that. The session
    // is deliberately NOT aborted here, so the final transcript still lands in the
    // field; `onend` cleans it up immediately afterwards.
    recognition.onspeechend = () => {
      this.setListening(false);
      this.cdr.markForCheck();
    };
    recognition.onend = () => {
      this.setListening(false);
      // Release the session so the next mic tap opens a fresh one rather than
      // being read as "stop" by `toggleVoice`.
      this.recognition = undefined;
      this.cdr.markForCheck();
    };
    recognition.onerror = () => {
      this.setListening(false);
      this.recognition = undefined;
      this.cdr.markForCheck();
    };
    this.recognition = recognition;
    this.setListening(true);
    recognition.start();
  }

  private stopVoice(): void {
    if (this.recognition) {
      // Detach handlers BEFORE aborting so no trailing onresult/onend can fire
      // back into a component that's already moved on (e.g. after submit clears
      // the field). `abort()` cancels without emitting a final result.
      this.recognition.onresult = null;
      this.recognition.onspeechend = null;
      this.recognition.onend = null;
      this.recognition.onerror = null;
      this.recognition.abort();
      this.recognition = undefined;
    }
    this.setListening(false);
  }

  private setListening(value: boolean): void {
    if (this.listening === value) {
      return;
    }
    this.listening = value;
    this.announcer.announce(value ? 'Listening' : 'Listening stopped.');
  }

  /**
   * Strips the newlines a paste would otherwise hide the text behind (see
   * `onPaste`). CRLF/CR are normalized first so a Windows or classic-Mac
   * clipboard is treated the same as any other.
   *
   * Single-line (entry bar): every newline goes — leading/trailing ones dropped,
   * interior ones become a space, so a two-line copy still reads as one question
   * instead of silently losing its second half.
   * Multi-line (panel): only the ends are trimmed. An interior newline is
   * deliberate content there.
   */
  private withoutStrayNewlines(text: string): string {
    const normalized = text.replace(/\r\n?/g, '\n').replace(/^\n+|\n+$/g, '');
    return this.singleLine ? normalized.replace(/\n+/g, ' ') : normalized;
  }

  /**
   * Replaces the field's selection with `text`, as a paste does. Written through
   * the FormControl (rather than the DOM) so `valueChanges` — and with it the
   * empty-state the host uses for the suggested prompts — sees the new value; the
   * accessor writes it straight back to the textarea, which resets the caret to
   * the end of the whole value, so the caret is restored to where the paste
   * finished. Only reached for a newline-carrying paste.
   */
  private insertAtCaret(text: string): void {
    const field = this.field?.nativeElement;
    if (!field) {
      return;
    }
    const value = this.control.value;
    const start = field.selectionStart ?? value.length;
    const end = field.selectionEnd ?? start;
    const caret = start + text.length;
    this.control.setValue(value.slice(0, start) + text + value.slice(end));
    field.setSelectionRange(caret, caret);
    // The scroll that hid the text in the first place: a field whose value is now
    // a single line has nothing to scroll to, so pin it back to the top in case
    // the browser had already scrolled past it.
    if (!this.control.value.includes('\n')) {
      field.scrollTop = 0;
    }
    this.cdr.markForCheck();
  }

  /**
   * Size the field to its own content, up to the cap the stylesheet sets
   * (PCSA-13548 AC 1-3).
   *
   * A `rows="1"` textarea is one line tall and STAYS one line tall however much is
   * typed into it — CSS has no way to size a textarea to its content, so the box is
   * measured here and the height written back. Until now the field's `max-height`
   * capped growth that never happened: a four-line prompt sat in a one-line box and
   * the browser scrolled it to follow the caret, so the user could only ever see
   * the line they were on.
   *
   * `height: auto` FIRST, then `scrollHeight`. The reset is what lets the field
   * shrink again when a line is deleted: `scrollHeight` is the content height or the
   * current box height, whichever is larger, so measuring without collapsing first
   * would ratchet the field taller and never let it back down. Collapsing costs a
   * scrolled field nothing, either — a shorter box only ever makes MORE of the
   * content scrollable, so the offset the caret is at stays in range throughout.
   *
   * The cap itself stays in the stylesheet as `max-height` rather than being
   * computed here (AC 2). `max-height` beats an inline `height` whatever we write,
   * so a field at the cap simply stops growing and its `overflow-y` takes over
   * (AC 3/4) with no second code path — and the one number a designer may retune
   * stays in the file they would look in.
   *
   * The entry bar (`singleLine`) is exempt: it is one line by definition and states
   * its own fixed height, which an inline height would fight. Its overflow is
   * hidden and its text ellipsized, so there is no growth to measure.
   */
  private autoGrow(): void {
    const field = this.field?.nativeElement;
    if (!field || this.singleLine) {
      return;
    }
    field.style.height = 'auto';
    field.style.height = `${field.scrollHeight}px`;
  }

  /** Resolves the SpeechRecognition constructor (standard or webkit-prefixed). */
  private getSpeechRecognitionCtor():
    | (new () => OrbiSpeechRecognition)
    | undefined {
    const w = window as unknown as {
      SpeechRecognition?: new () => OrbiSpeechRecognition;
      webkitSpeechRecognition?: new () => OrbiSpeechRecognition;
    };
    return w.SpeechRecognition || w.webkitSpeechRecognition;
  }
}
